// See the current list of valid CCP product IDS here:
import { type IgnoreValidation } from '@atlassian/vindicated';
import type { SerializablePayload, SerializableRecord } from '@post-office/serializable-record';

// This is a constant used to standardized the value of the "any" field in the message metadata
const AnyValue = 'any';

// https://hello.atlassian.net/wiki/spaces/ECON/pages/2339030895/App+Identifiers
export const JiraSoftwareCcpId = 'd8a847a4-cde4-4c50-8ea1-dc3d4193214f';
export const JiraServiceManagementCcpId = 'de63a248-1f93-46ed-b6ea-ab8e38af1c88';
export const JiraWorkManagementCloudCcpId = '46ccaa9e-cdb0-4995-8886-5827a5ff162b';
export const JiraProductDiscoveryCcpId = 'a6f908c8-709c-45f4-9e2d-eca25b93b94a';
export const ConfluenceCloudCcpId = '6aab3f4e-cc71-474d-8d28-abef8b057808';
export const StatuspageCcpId = '0b8ea1e2-52df-11ea-8d77-2e728ce88125';
export const AtlasCcpId = '7c917705-866d-4b6b-b266-0fc7633e8342';
export const CompassCcpId = '6bce2ccb-4f57-46f5-812a-9dbf02dc28f2';
export const AtlassianAnalyticsCcpId = 'f44da23e-5907-4d81-8a78-33bedee10969';
export const AtlassianWhiteboardCcpId = 'b6c5632b-6018-4656-b2ac-3e32e7cef0d3';
export const OpsgenieCcpId = 'fdb4649d-ca4d-49f8-a8bf-ffa558a65551';
export const RovoCcpId = '91ae3e3f-138e-43be-98cc-48df03d13c32';
export const RovoDevCcpId = '56cce7cb-8c54-4103-8736-e64d1ab00128';
export const LoomCcpId = '7683a73e-423d-41a1-b43f-d497656f8b5d';
export const FocusCcpId = 'd16a2ba8-445f-4e83-9c7b-9bcf349ff905';
export const AnyProductCcpId: 'any' = AnyValue;
export const TestProductCcpId = 'fake-id';

export const ValidCcpId: {
	readonly JiraSoftwareCcpId: 'd8a847a4-cde4-4c50-8ea1-dc3d4193214f';
	readonly JiraServiceManagementCcpId: 'de63a248-1f93-46ed-b6ea-ab8e38af1c88';
	readonly JiraWorkManagementCloudCcpId: '46ccaa9e-cdb0-4995-8886-5827a5ff162b';
	readonly JiraProductDiscoveryCcpId: 'a6f908c8-709c-45f4-9e2d-eca25b93b94a';
	readonly ConfluenceCloudCcpId: '6aab3f4e-cc71-474d-8d28-abef8b057808';
	readonly StatuspageCcpId: '0b8ea1e2-52df-11ea-8d77-2e728ce88125';
	readonly AtlasCcpId: '7c917705-866d-4b6b-b266-0fc7633e8342';
	readonly CompassCcpId: '6bce2ccb-4f57-46f5-812a-9dbf02dc28f2';
	readonly AtlassianAnalyticsCcpId: 'f44da23e-5907-4d81-8a78-33bedee10969';
	readonly AtlassianWhiteboardCcpId: 'b6c5632b-6018-4656-b2ac-3e32e7cef0d3';
	readonly OpsgenieCcpId: 'fdb4649d-ca4d-49f8-a8bf-ffa558a65551';
	readonly RovoCcpId: '91ae3e3f-138e-43be-98cc-48df03d13c32';
	readonly RovoDevCcpId: '56cce7cb-8c54-4103-8736-e64d1ab00128';
	readonly LoomCcpId: '7683a73e-423d-41a1-b43f-d497656f8b5d';
	readonly FocusCcpId: 'd16a2ba8-445f-4e83-9c7b-9bcf349ff905';
	readonly AnyProductCcpId: 'any';
	readonly TestProductCcpId: 'fake-id';
} = {
	JiraSoftwareCcpId,
	JiraServiceManagementCcpId,
	JiraWorkManagementCloudCcpId,
	JiraProductDiscoveryCcpId,
	ConfluenceCloudCcpId,
	StatuspageCcpId,
	AtlasCcpId,
	CompassCcpId,
	AtlassianAnalyticsCcpId,
	AtlassianWhiteboardCcpId,
	OpsgenieCcpId,
	RovoCcpId,
	RovoDevCcpId,
	LoomCcpId,
	FocusCcpId,
	AnyProductCcpId,
	TestProductCcpId,
} as const;

// this type takes all of the values of the ValidCcpId
// it allows use of either the string value or the const itself
export type ValidProductCcpId = IgnoreValidation<(typeof ValidCcpId)[keyof typeof ValidCcpId]>;

/**
 * productId: This needs to be added for app-switcher message templates.
 *            It should be the product related to the message itself.
 *            This ID is used by the recommendations service.
 */
export type MessageMetadata = SerializableRecord & {
	productId?: ValidProductCcpId;
};

export type TransformMetadataResult = {
	rerankingMetadata?: Record<string, string | number | boolean>;
	goalMetadata: GoalMetadata;
	[K: string]: SerializablePayload;
};

export const FreeEdition = 'free';
export const StandardEdition = 'standard';
export const PremiumEdiiton = 'premium';
export const AnyEdition: 'any' = AnyValue;

export const ValidEdition: {
	readonly FreeEdition: 'free';
	readonly StandardEdition: 'standard';
	readonly PremiumEdiiton: 'premium';
	readonly AnyEdition: 'any';
} = {
	FreeEdition,
	StandardEdition,
	PremiumEdiiton,
	AnyEdition,
} as const;

// this type takes all of the values of the ValidEdition
// it allows use of either the string value or the const itself
export type ValidProductEdition = IgnoreValidation<
	(typeof ValidEdition)[keyof typeof ValidEdition]
>;

export const SignupGoal = 'signup';
export const D1to6aiGoal = 'd1to6ai';
export const FfpGoal = 'ffp';
export const FoGoal = 'fo';
export const McuGoal = 'mcu';
export const MauGoal = 'mau';
export const MacmGoal = 'macm'; // Month Active Community Member
export const PurchaseGoal = 'purchase';
export const M2AtiGoal = 'm2_ati';
export const PeuGoal = 'peu';
export const EventRegistrationGoal = 'event_registration';
export const TechAlertGoal = 'tech_alert';
export const CommerceNotificationGoal = 'commerce_notification';
export const WebinarInvitesGoal = 'webinar_invites';
export const DemoGoal = 'demo';
export const SurveryResearchGoal = 'survey_research';
export const ClickthroughGoal = 'clickthrough';

export const ValidGoalIdentifiers: {
	readonly SignupGoal: 'signup';
	readonly D1to6aiGoal: 'd1to6ai';
	readonly FfpGoal: 'ffp';
	readonly FoGoal: 'fo';
	readonly McuGoal: 'mcu';
	readonly MauGoal: 'mau';
	readonly MacmGoal: 'macm';
	readonly PurchaseGoal: 'purchase';
	readonly M2AtiGoal: 'm2_ati';
	readonly PeuGoal: 'peu';
	readonly EventRegistrationGoal: 'event_registration';
	readonly TechAlertGoal: 'tech_alert';
	readonly CommerceNotificationGoal: 'commerce_notification';
	readonly WebinarInvitesGoal: 'webinar_invites';
	readonly DemoGoal: 'demo';
	readonly SurveryResearchGoal: 'survey_research';
	readonly ClickthroughGoal: 'clickthrough';
} = {
	SignupGoal,
	D1to6aiGoal,
	FfpGoal,
	FoGoal,
	McuGoal,
	MauGoal,
	MacmGoal,
	PurchaseGoal,
	M2AtiGoal,
	PeuGoal,
	EventRegistrationGoal,
	TechAlertGoal,
	CommerceNotificationGoal,
	WebinarInvitesGoal,
	DemoGoal,
	SurveryResearchGoal,
	ClickthroughGoal,
} as const;

// this type takes all of the values of the ValidGoalIdentifiers
// it allows use of either the string value or the const itself
export type ValidGoalId = IgnoreValidation<
	(typeof ValidGoalIdentifiers)[keyof typeof ValidGoalIdentifiers]
>;

/**
 * The device details object allows for filtering on a
 * user's device. To read more check out the socrates docs:
 * https://developer.atlassian.com/platform/analytics-pipeline/guides/for-data-users/socrates/#track--ui---operational
 */
export type DeviceDetails = {
	deviceType: string;
	deviceBrand: string;
	device: string;
	deviceFamily: string;
};

/**
 * Post Office currently only supports GASv3 analytics events as a source
 * The data field is where message creators are expected to enter information
 * about the GASv3 they wish to use as their message instance's success metric
 */
export type MessageGoalMetadata = {
	source: 'gasv3';
	data: MessageGoalMetadataGasV3;
};

// We are currently only supporting these event types
// We are explicitly excluding 'operational' events to help
// guiude message creators towards using in-product analytics events
export const UIEventTypeGASv3 = 'ui';
export const TrackEventTypeGASv3 = 'track';
export const ScreenEventTypeGASv3 = 'screen';

const ValidGASv3EventTypes: {
	readonly UIEventTypeGASv3: 'ui';
	readonly TrackEventTypeGASv3: 'track';
	readonly ScreenEventTypeGASv3: 'screen';
} = {
	UIEventTypeGASv3,
	TrackEventTypeGASv3,
	ScreenEventTypeGASv3,
} as const;

/**
 * The analytic event types that Post Office is supporting
 * https://developer.atlassian.com/platform/analytics-pipeline/guides/for-data-users/socrates/#introduction
 */
export type MessageGoalGasV3EventTypes =
	(typeof ValidGASv3EventTypes)[keyof typeof ValidGASv3EventTypes];

/**
 * The structure of screen GASv3 events differs slightly from the UI and track event types
 * For more information:
 * https://developer.atlassian.com/platform/analytics-pipeline/guides/for-data-users/socrates/#introduction
 */
export type MessageGoalMetadataGasV3 =
	| {
			eventType: IgnoreValidation<Extract<MessageGoalGasV3EventTypes, 'screen'>>;
			eventName: string;
			product: string;
			eventAttributes?: Record<string, string>;
			deviceDetails?: DeviceDetails;
	  }
	| {
			eventType: IgnoreValidation<Extract<MessageGoalGasV3EventTypes, 'ui' | 'track'>>;
			action: string;
			actionSubject: string;
			eventName: string;
			product: string;
			eventAttributes?: Record<string, string>;
			deviceDetails?: DeviceDetails;
	  };

// BasicGoals are goals that do not have any additional required fields
export interface BasicGoal {
	goalId:
		| 'event_registration'
		| 'tech_alert'
		| 'commerce_notification'
		| 'webinar_invites'
		| 'demo'
		| 'survey_research'
		| 'clickthrough';
}

export const ValidTargetProductGoalIdentifiers: {
	readonly SignupGoal: 'signup';
	readonly D1to6aiGoal: 'd1to6ai';
	readonly FfpGoal: 'ffp';
	readonly FoGoal: 'fo';
	readonly McuGoal: 'mcu';
	readonly MauGoal: 'mau';
	readonly PurchaseGoal: 'purchase';
	readonly M2AtiGoal: 'm2_ati';
	readonly PeuGoal: 'peu';
} = {
	SignupGoal,
	D1to6aiGoal,
	FfpGoal,
	FoGoal,
	McuGoal,
	MauGoal,
	PurchaseGoal,
	M2AtiGoal,
	PeuGoal,
} as const;

// this type takes all of the values of the ValidGoalIdentifiers
// it allows use of either the string value or the const itself
export type ValidTargetProductGoalId = IgnoreValidation<
	(typeof ValidTargetProductGoalIdentifiers)[keyof typeof ValidTargetProductGoalIdentifiers]
>;

// TargetProductGoals are goals that are required to have the targetProduct and edition fields
export interface TargetProductGoal {
	goalId: ValidTargetProductGoalId;
	targetProduct: ValidProductCcpId[];
	edition: ValidProductEdition[];
}

export type BusinessGoalMetadata = SerializableRecord & (TargetProductGoal | BasicGoal);

export type GoalMetadata = {
	primaryGoal: BusinessGoalMetadata;
	secondaryGoal?: BusinessGoalMetadata;
	successMetric?: MessageGoalMetadata[];
};

// PO-3567: This should be changed once all message templates have goal. Instead we should be using the TransformMetadataResult type
export const createTransformMetadataForNonInappChannels = <T extends SerializableRecord>() => {
	return (
		transformFn: (props: { messageContext: T }) => TransformMetadataResult,
	): ((props: { messageContext: T }) => TransformMetadataResult) => {
		return transformFn;
	};
};

// Format the product name to be more readable
const formatProductName = (ccpId: ValidProductCcpId): string => {
	// Find the constant name from the ccpId
	const [ccpIdKey] = Object.entries(ValidCcpId).find(([_, value]) => value === ccpId) ?? [];
	if (!ccpIdKey) return ccpId;
	const nameWithoutSuffix = ccpIdKey.replace('CcpId', '');
	return nameWithoutSuffix.replace(/([A-Z])/g, ' $1').trim();
};

// List of all target products, with their ccpId and display name
export const goalTargetProducts: {
	id:
		| 'd8a847a4-cde4-4c50-8ea1-dc3d4193214f'
		| 'de63a248-1f93-46ed-b6ea-ab8e38af1c88'
		| '46ccaa9e-cdb0-4995-8886-5827a5ff162b'
		| 'a6f908c8-709c-45f4-9e2d-eca25b93b94a'
		| '6aab3f4e-cc71-474d-8d28-abef8b057808'
		| '0b8ea1e2-52df-11ea-8d77-2e728ce88125'
		| '7c917705-866d-4b6b-b266-0fc7633e8342'
		| '6bce2ccb-4f57-46f5-812a-9dbf02dc28f2'
		| 'f44da23e-5907-4d81-8a78-33bedee10969'
		| 'b6c5632b-6018-4656-b2ac-3e32e7cef0d3'
		| 'fdb4649d-ca4d-49f8-a8bf-ffa558a65551'
		| '91ae3e3f-138e-43be-98cc-48df03d13c32'
		| '56cce7cb-8c54-4103-8736-e64d1ab00128'
		| '7683a73e-423d-41a1-b43f-d497656f8b5d'
		| 'd16a2ba8-445f-4e83-9c7b-9bcf349ff905';
	displayName: string;
}[] = Object.values(ValidCcpId)
	.filter((id) => id !== ValidCcpId.TestProductCcpId && id !== ValidCcpId.AnyProductCcpId)
	.map((id) => ({
		id: id,
		displayName: formatProductName(id),
	}));

export const isTargetProductGoal = (goalId: ValidGoalId): goalId is ValidTargetProductGoalId =>
	Object.values(ValidTargetProductGoalIdentifiers).includes(goalId as ValidTargetProductGoalId);
